import { useEffect, useState } from 'react';
import c from '../../../constant';
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, Button, Box, Flex, Text, useToast } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import 'moment/locale/en-gb';
import DateRangeFormField from '../../../components/form/DateRangeFormField';
import { gql, useLazyQuery } from '@apollo/client';
import useStore from '../../../store';
import ExportExcelPositionStatus from './excel';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

const QUERY_EXPORT_POSITION_STATUS = gql`
	query exportPositionStatus($input: PositionStatusExport!) {
		exportPositionStatus(input: $input) {
			id
			no
			position
			organisation
			rank
			dateOfLastPosition
			dateOfLastRank
			department
			office
			createdBy
			createdAt
			dateStart
			dateEnd
			user {
				id
				fullName
				fullNameKhmer
				department
				office
				position
			}
		}
	}
`;

const filterSchema = Yup.object().shape({
	createdDate: Yup.string().nullable(),
});

function PositionStatusExport({ isOpen, onClose }) {
	moment.locale('en-gb');
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const location = useLocation();
	const urlParams = new URLSearchParams(location.search);
	const profileUserId = urlParams.get('userId') ? urlParams.get('userId') : null;
	const toast = useToast();
	const [clearKey, setClearKey] = useState(0);
	const [exportPositionStatus, { data, loading }] = useLazyQuery(QUERY_EXPORT_POSITION_STATUS, {
		fetchPolicy: 'network-only',
	});

	useEffect(() => {
		if (data?.exportPositionStatus?.length === 0) {
			toast({
				title: t('Position Status does not exist'),
				status: 'warning',
				isClosable: true,
				position: 'top-right',
				duration: 3000,
			});
		}
	}, [data]);

	function callbackExport(values) {
		let dateStart = null;
		let dateEnd = null;

		if (values?.createdDate) {
			const tmpDate = JSON.parse(values.createdDate);

			if (tmpDate?.from && tmpDate?.to) {
				const inputFrom = {
					day: tmpDate?.from?.day,
					month: tmpDate?.from?.month - 1,
					year: tmpDate?.from?.year,
				};

				const inputTo = {
					day: tmpDate?.to?.day,
					month: tmpDate?.to?.month - 1,
					year: tmpDate?.to?.year,
				};

				dateStart = moment(inputFrom).startOf('day').toDate();
				dateEnd = moment(inputTo).endOf('day').toDate();
			}
		}

		if (!dateStart || !dateEnd) {
			dateStart = moment().startOf('month').toDate();
			dateEnd = moment().toDate();
		}

		exportPositionStatus({
			variables: {
				input: {
					userId: profileUserId,
					dateStart: dateStart && dateEnd ? dateStart : null,
					dateEnd: dateStart && dateEnd ? dateEnd : null,
				},
			},
		});
	}

	return (
		<Modal borderRadius={c.borderRadius} isOpen={isOpen} onClose={onClose}>
			<ModalOverlay />
			<ModalContent borderRadius={c.borderRadius}>
				<ModalHeader>{t('Export Position Status')}</ModalHeader>
				<ModalCloseButton />

				<ModalBody>
					<Formik
						initialValues={{
							createdDate: '',
						}}
						validationSchema={filterSchema}
						onSubmit={callbackExport}
					>
						{(props) => (
							<Box pb="4">
								<Form>
									<center>
										<DateRangeFormField
											key={`createdDate-${clearKey}`}
											{...props}
											label="Created Date"
											name="createdDate"
											initialDate={{ fromDate: '', toDate: '' }}
										/>{' '}
									</center>

									<Flex mt="32px" justifyContent="flex-end">
										<Button
											variant="solid"
											size="md"
											h="42px"
											w="128px"
											type="submit"
											borderRadius={c.borderRadius}
											color="#3498db"
											isLoading={loading}
										>
											{t('Get Data')}
										</Button>

										<Button
											type="button"
											size="md"
											h="42px"
											ml={4}
											w="128px"
											onClick={() => {
												props.resetForm();
												setClearKey((prev) => prev + 1);
											}}
											variant="ghost"
										>
											{t('Clear')}
										</Button>
									</Flex>
								</Form>

								{data?.exportPositionStatus?.length > 0 && (
									<Box mt="24px" borderTop="1px solid #e2e8f0" pt="16px">
										<Flex alignItems="center" justifyContent="space-between">
											<Text>
												{t('Total Rows')}: {data?.exportPositionStatus?.length}
											</Text>
											<ExportExcelPositionStatus records={data?.exportPositionStatus} />
										</Flex>
									</Box>
								)}
							</Box>
						)}
					</Formik>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}

export default PositionStatusExport;
